/**
 * Export utilities for generating CSV, Excel, and JSON files
 * Uses xlsx library for Excel and file-saver for downloads
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import type { PlaceListItem, Place } from '@/types/place';
import type { ExportFormat } from '@/types/place';

/**
 * Column definitions for export
 */
export interface ExportColumn {
  key: keyof PlaceListItem | keyof Place;
  header: string;
  width?: number;
}

/**
 * Default columns for export
 */
export const DEFAULT_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'name', header: 'Name', width: 30 },
  { key: 'address', header: 'Address', width: 50 },
  { key: 'phone', header: 'Phone', width: 20 },
  { key: 'website', header: 'Website', width: 40 },
  { key: 'rating', header: 'Rating', width: 10 },
  { key: 'reviewCount', header: 'Reviews', width: 10 },
  { key: 'category', header: 'Category', width: 25 },
  { key: 'priceLevel', header: 'Price Level', width: 12 },
  { key: 'latitude', header: 'Latitude', width: 15 },
  { key: 'longitude', header: 'Longitude', width: 15 },
  { key: 'googleMapsUrl', header: 'Google Maps URL', width: 60 },
];

/**
 * Extended columns including all available fields
 */
export const ALL_EXPORT_COLUMNS: ExportColumn[] = [
  ...DEFAULT_EXPORT_COLUMNS,
  { key: 'placeId', header: 'Place ID', width: 30 },
  { key: 'plusCode', header: 'Plus Code', width: 15 },
  { key: 'currentStatus', header: 'Current Status', width: 20 },
  { key: 'ownerVerified', header: 'Owner Verified', width: 15 },
  { key: 'description', header: 'Description', width: 50 },
  { key: 'scrapedAt', header: 'Scraped At', width: 20 },
];

/**
 * Convert places data to rows for export
 */
function placesToRows(
  places: (PlaceListItem | Place)[],
  columns: ExportColumn[]
): Record<string, unknown>[] {
  return places.map((place) => {
    const row: Record<string, unknown> = {};
    columns.forEach((col) => {
      const value = place[col.key as keyof typeof place];

      // Handle special formatting
      if (col.key === 'openingHours' && value) {
        row[col.header] = formatOpeningHours(value as Record<string, string>);
      } else if (col.key === 'amenities' && Array.isArray(value)) {
        row[col.header] = (value as string[]).join(', ');
      } else if (col.key === 'serviceOptions' && Array.isArray(value)) {
        row[col.header] = (value as string[]).join(', ');
      } else if (col.key === 'images' && Array.isArray(value)) {
        row[col.header] = (value as string[]).join('\n');
      } else if (col.key === 'ownerVerified') {
        row[col.header] = value ? 'Yes' : 'No';
      } else if (value === null || value === undefined) {
        row[col.header] = '';
      } else {
        row[col.header] = value;
      }
    });
    return row;
  });
}

/**
 * Format opening hours object to string
 */
function formatOpeningHours(hours: Record<string, string>): string {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  return days
    .filter((day) => hours[day])
    .map((day) => `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hours[day]}`)
    .join('; ');
}

/**
 * Escape CSV value
 */
function escapeCSV(value: unknown): string {
  if (value === null || value === undefined) return '';

  const str = String(value);

  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

/**
 * Export places to CSV format
 */
export function exportToCSV(
  places: (PlaceListItem | Place)[],
  filename: string,
  columns: ExportColumn[] = DEFAULT_EXPORT_COLUMNS
): void {
  const rows = placesToRows(places, columns);
  const headers = columns.map((col) => col.header);

  // Build CSV content
  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map((row) =>
      headers.map((header) => escapeCSV(row[header])).join(',')
    ),
  ].join('\n');

  // Create blob with BOM for Excel compatibility
  const blob = new Blob(['\ufeff' + csvContent], {
    type: 'text/csv;charset=utf-8',
  });

  saveAs(blob, `${filename}.csv`);
}

/**
 * Export places to Excel format
 */
export function exportToExcel(
  places: (PlaceListItem | Place)[],
  filename: string,
  columns: ExportColumn[] = DEFAULT_EXPORT_COLUMNS
): void {
  const rows = placesToRows(places, columns);

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Set column widths
  worksheet['!cols'] = columns.map((col) => ({ wch: col.width || 15 }));

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Results');

  // Write to buffer and save
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, `${filename}.xlsx`);
}

/**
 * Export places to JSON format
 */
export function exportToJSON(
  places: (PlaceListItem | Place)[],
  filename: string,
  pretty = true
): void {
  const jsonContent = pretty
    ? JSON.stringify(places, null, 2)
    : JSON.stringify(places);

  const blob = new Blob([jsonContent], {
    type: 'application/json',
  });

  saveAs(blob, `${filename}.json`);
}

/**
 * Export places to specified format
 */
export function exportPlaces(
  places: (PlaceListItem | Place)[],
  filename: string,
  format: ExportFormat,
  columns?: ExportColumn[]
): void {
  switch (format) {
    case 'csv':
      exportToCSV(places, filename, columns);
      break;
    case 'xlsx':
      exportToExcel(places, filename, columns);
      break;
    case 'json':
      exportToJSON(places, filename);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

/**
 * Generate a filename for export
 */
export function generateExportFilename(
  jobQuery: string,
  locationName?: string,
  format?: ExportFormat
): string {
  const sanitizedQuery = jobQuery
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 30);

  const sanitizedLocation = locationName
    ? locationName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 20)
    : '';

  const timestamp = new Date().toISOString().slice(0, 10);

  const baseName = sanitizedLocation
    ? `${sanitizedQuery}-${sanitizedLocation}-${timestamp}`
    : `${sanitizedQuery}-${timestamp}`;

  return format ? `${baseName}.${format}` : baseName;
}

/**
 * Get file size estimate for export
 */
export function estimateExportSize(
  placeCount: number,
  format: ExportFormat
): string {
  // Rough estimates per place
  const bytesPerPlace: Record<ExportFormat, number> = {
    csv: 500,
    xlsx: 400,
    json: 800,
  };

  const totalBytes = placeCount * bytesPerPlace[format];

  if (totalBytes < 1024) {
    return `${totalBytes} B`;
  } else if (totalBytes < 1024 * 1024) {
    return `${(totalBytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

/**
 * Check if export is too large for client-side processing
 */
export function isExportTooLarge(placeCount: number): boolean {
  // Recommend server-side export for more than 10,000 places
  return placeCount > 10000;
}
